import { useTranslation } from 'react-i18next';
import { ListDivider } from '@/components/List';
import { FilterList } from '@/components/FilterList';
import { SourceFolder } from '@/types/source-folder';
import { SourcesSelection, SourceType } from '@/types/source';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { SourcesFolderList } from './SourcesFolderList';
import type { TypeFilter } from '../hooks/useSourcesFilters';
import type { FolderSortOption } from '../hooks/useFolderSort';

export interface SourcesNavProps {
    myFolders: SourceFolder[];
    sharedFolders: SourceFolder[];
    allSourcesCount: number;
    sharedSourcesCount: number;
    selection: SourcesSelection;
    onSelectionChange: (selection: SourcesSelection) => void;
    typeFilter: TypeFilter;
    onTypeFilterClear: () => void;
    onCreateFolderClick: () => void;
    onEditFolder?: (folder: SourceFolder) => void;
    onDeleteFolder?: (folder: SourceFolder) => void;
    myFoldersSortOption?: FolderSortOption;
    onMyFoldersSortChange?: (option: FolderSortOption) => void;
    sharedFoldersSortOption?: FolderSortOption;
    onSharedFoldersSortChange?: (option: FolderSortOption) => void;
    isLoadingGroups?: boolean;
    hasMoreMyFolders?: boolean;
    onLoadMoreMyFolders?: () => void;
    isLoadingMoreMyFolders?: boolean;
    hasMoreSharedFolders?: boolean;
    onLoadMoreSharedFolders?: () => void;
    isLoadingMoreSharedFolders?: boolean;
}

interface SourcesSidebarProps {
    navProps: SourcesNavProps;
}

export function SourcesSidebar({ navProps }: SourcesSidebarProps) {
    const {
        myFolders,
        sharedFolders,
        allSourcesCount,
        sharedSourcesCount,
        selection,
        onSelectionChange,
        typeFilter,
        onTypeFilterClear,
        onCreateFolderClick,
        onEditFolder,
        onDeleteFolder,
        myFoldersSortOption,
        onMyFoldersSortChange,
        sharedFoldersSortOption,
        onSharedFoldersSortChange,
        isLoadingGroups,
        hasMoreMyFolders,
        onLoadMoreMyFolders,
        isLoadingMoreMyFolders,
        hasMoreSharedFolders,
        onLoadMoreSharedFolders,
        isLoadingMoreSharedFolders,
    } = navProps;
    const { t } = useTranslation([I18nNamespace.SOURCES, I18nNamespace.COMMON]);

    const typeFilterCategory =
        typeFilter !== 'all'
            ? {
                  name: t('filters.title'),
                  items: [
                      {
                          label:
                              typeFilter === SourceType.CAMERA
                                  ? t('filters.cameras')
                                  : t('filters.files'),
                          onRemove: onTypeFilterClear,
                      },
                  ],
                  onClearAll: onTypeFilterClear,
              }
            : null;

    return (
        <div className="w-full flex flex-col gap-6">
            <SourcesFolderList
                myFolders={myFolders}
                sharedFolders={sharedFolders}
                allSourcesCount={allSourcesCount}
                sharedSourcesCount={sharedSourcesCount}
                selection={selection}
                onSelectionChange={onSelectionChange}
                onCreateFolderClick={onCreateFolderClick}
                onEditFolder={onEditFolder}
                onDeleteFolder={onDeleteFolder}
                myFoldersSortOption={myFoldersSortOption}
                onMyFoldersSortChange={onMyFoldersSortChange}
                sharedFoldersSortOption={sharedFoldersSortOption}
                onSharedFoldersSortChange={onSharedFoldersSortChange}
                isLoadingGroups={isLoadingGroups}
                hasMoreMyFolders={hasMoreMyFolders}
                onLoadMoreMyFolders={onLoadMoreMyFolders}
                isLoadingMoreMyFolders={isLoadingMoreMyFolders}
                hasMoreSharedFolders={hasMoreSharedFolders}
                onLoadMoreSharedFolders={onLoadMoreSharedFolders}
                isLoadingMoreSharedFolders={isLoadingMoreSharedFolders}
            />
            {typeFilterCategory && (
                <>
                    <ListDivider />
                    <FilterList
                        title={t('common:browseBar.activeFilters')}
                        categories={[typeFilterCategory]}
                    />
                </>
            )}
        </div>
    );
}
